import { useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Alert, Box, Stack, Typography } from "@mui/material";
import { ApiError, fileUrl } from "../../../shared/api/client";
import { AppBadge, AppButton, AppField, AppTextarea, ConfirmAction } from "../../../shared/components/ui";
import { PageHeader, PageState } from "../../../shared/components/PageChrome";
import {
  DecisionLayout,
  DecisionPanel,
  DetailPanel,
  DetailRows,
  NotifyPreview,
  PhotoGrid,
  PhotoThumb,
  ReasonPicker,
} from "../../../shared/components/DecisionChrome";
import { IconCamera, IconCheck, IconSpinner, IconWarning } from "../../../shared/components/icons";
import { decideKycSubmission, getKycSubmission, maskNida } from "../api";
import {
  KYC_CORRECTION_REASONS,
  KYC_CORRECTION_REASON_LABELS,
  type KycApproveResponse,
  type KycCorrectionReason,
  type KycSubmissionDetail,
} from "../types";

/** Photo names captured on this device by the field flow, if any. */
function localPhotoNames(leadId: number): string[] {
  try {
    const raw = localStorage.getItem("pg_field_leads_v1");
    if (!raw) return [];
    const leads = JSON.parse(raw) as { serverId?: number; photos?: { customer?: string; house?: string } }[];
    const rec = leads.find((l) => l.serverId === leadId);
    return [rec?.photos?.customer, rec?.photos?.house].filter((p): p is string => Boolean(p));
  } catch {
    return [];
  }
}

function geofenceBadge(state: string | undefined) {
  if (state === "in_range")
    return (
      <AppBadge tone="success">
        <IconCheck size={12} /> In range
      </AppBadge>
    );
  if (state === "out_of_range")
    return (
      <AppBadge tone="warning">
        <IconWarning size={12} /> Out of range
      </AppBadge>
    );
  return <AppBadge tone="neutral">Unknown</AppBadge>;
}

export function S6KycDecision() {
  const { id = "" } = useParams();
  const submissionId = Number(id);
  const navigate = useNavigate();

  const [mode, setMode] = useState<"decide" | "correction">("decide");
  const [reasons, setReasons] = useState<KycCorrectionReason[]>([]);
  const [note, setNote] = useState("");
  const [formError, setFormError] = useState<string | null>(null);
  const [idempotencyKey] = useState(() => crypto.randomUUID());
  const [confirmApprove, setConfirmApprove] = useState(false);

  const query = useQuery({
    queryKey: ["kyc-submission", submissionId],
    queryFn: () => getKycSubmission(submissionId),
    retry: 1,
    staleTime: 15_000,
  });

  const mutation = useMutation({
    mutationFn: (body: Parameters<typeof decideKycSubmission>[1]) =>
      decideKycSubmission(submissionId, body, idempotencyKey),
  });

  const photos = useMemo(
    () => (query.data ? localPhotoNames(query.data.submission.lead_id) : []),
    [query.data],
  );

  if (query.isPending) {
    return <PageState status="loading" />;
  }

  if (query.isError) {
    return (
      <PageState
        status="error"
        error={query.error instanceof Error ? query.error.message : "Could not load submission"}
        onRetry={() => void query.refetch()}
      />
    );
  }

  const sub = query.data.submission;
  const fallbackName = `${sub.payload.first_name ?? ""} ${sub.payload.last_name ?? ""}`.trim();
  const name = sub.customer_name ?? (fallbackName || "—");

  if (mutation.isSuccess) {
    const data = mutation.data;
    if ("customer" in data || "customer_id" in data) {
      const approved = data as KycApproveResponse;
      const customerId = approved.customer?.id ?? approved.customer_id ?? null;
      const customerRef =
        approved.customer?.customer_number ?? (approved.customer_id ? `#${approved.customer_id}` : "—");
      const contractRef =
        approved.contract?.contract_number ?? (approved.contract_id ? `#${approved.contract_id}` : "—");
      return (
        <Stack spacing={2} sx={{ py: 4, alignItems: "center", textAlign: "center" }}>
          <IconCheck size={40} />
          <Typography variant="h2">Customer created</Typography>
          <Typography color="text.secondary">
            Customer {customerRef} created. Contract draft {contractRef} ready. Rep notified.
          </Typography>
          {customerId ? (
            <AppButton type="button" onClick={() => navigate(`/customers/${customerId}?contract=activate`)}>
              Activate contract
            </AppButton>
          ) : null}
          <AppButton type="button" variant="secondary" onClick={() => navigate("/kyc-review")}>
            Back to queue
          </AppButton>
        </Stack>
      );
    }
    return (
      <Stack spacing={2} sx={{ py: 4, alignItems: "center", textAlign: "center" }}>
        <IconCheck size={40} />
        <Typography variant="h2">Correction requested</Typography>
        <Typography color="text.secondary">The rep will be notified with the reasons and your note.</Typography>
        <AppButton type="button" onClick={() => navigate("/kyc-review")}>
          Back to queue
        </AppButton>
      </Stack>
    );
  }

  const apiErr = mutation.error instanceof ApiError ? mutation.error : null;
  const conflict = apiErr?.status === 409;

  const submitApprove = () => {
    setFormError(null);
    setConfirmApprove(true);
  };

  const doApprove = () => {
    setConfirmApprove(false);
    mutation.mutate({ action: "approve" });
  };

  const submitCorrection = () => {
    setFormError(null);
    if (reasons.length === 0) {
      setFormError("Select at least one reason");
      return;
    }
    if (reasons.includes("other") && !note.trim()) {
      setFormError("A note is required when the reason is Other");
      return;
    }
    mutation.mutate({
      action: "request-correction",
      reasons,
      note: note.trim() || undefined,
    });
  };

  const toggleReason = (r: KycCorrectionReason) =>
    setReasons((prev) => (prev.includes(r) ? prev.filter((x) => x !== r) : [...prev, r]));

  const busy = mutation.isPending;

  return (
    <>
      <PageHeader
        title={`KYC — ${name}`}
        crumbs={[
          { label: "KYC Review", to: "/kyc-review" },
          { label: "Decision" },
        ]}
      />

      <DecisionLayout
        detail={<DetailCard sub={sub} photos={photos} />}
        decision={
          <DecisionPanel hint={mode === "decide" ? "Approve creates the customer and a contract draft." : undefined}>
            {conflict ? (
              <>
                <Alert severity="warning">{apiErr?.message ?? "This submission was already decided."}</Alert>
                <AppButton variant="secondary" type="button" onClick={() => navigate("/kyc-review")}>
                  Back to queue
                </AppButton>
              </>
            ) : mutation.isError ? (
              <>
                <Alert severity="error">
                  Decision could not be saved — {apiErr?.message ?? "network error"}. Nothing was recorded; retry.
                </Alert>
                <AppButton
                  variant="secondary"
                  type="button"
                  onClick={() => (mode === "correction" ? submitCorrection() : submitApprove())}
                  disabled={busy}
                >
                  {busy ? <IconSpinner size={14} /> : null} Retry
                </AppButton>
              </>
            ) : (
              <>
                <AppButton type="button" onClick={submitApprove} disabled={busy} style={{ width: "100%" }}>
                  {busy ? <IconSpinner size={14} /> : null} Approve — create customer
                </AppButton>

                {mode === "decide" ? (
                  <AppButton
                    variant="secondary"
                    type="button"
                    className="warning"
                    onClick={() => setMode("correction")}
                    disabled={busy}
                    style={{ width: "100%" }}
                  >
                    Request correction
                  </AppButton>
                ) : (
                  <>
                    <AppField label="Reason">
                      <ReasonPicker
                        options={KYC_CORRECTION_REASONS}
                        value={reasons}
                        onToggle={toggleReason}
                        labels={KYC_CORRECTION_REASON_LABELS}
                      />
                    </AppField>

                    <AppField label={reasons.includes("other") ? "Note to rep (required)" : "Note to rep"}>
                      <AppTextarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder="What should the rep fix?"
                      />
                    </AppField>

                    <NotifyPreview title="Notification preview (to rep)">
                      Correction requested for {name}:{" "}
                      {reasons.length > 0
                        ? reasons.map((r) => KYC_CORRECTION_REASON_LABELS[r]).join(", ")
                        : "…"}
                      {note.trim() ? ` — ${note.trim()}` : ""}
                    </NotifyPreview>

                    {formError ? (
                      <Alert severity="warning" icon={<IconWarning size={14} />}>
                        {formError}
                      </Alert>
                    ) : null}

                    <AppButton
                      type="button"
                      className="warning"
                      onClick={submitCorrection}
                      disabled={busy}
                      style={{ width: "100%" }}
                    >
                      {busy ? <IconSpinner size={14} /> : null} Send correction request
                    </AppButton>
                  </>
                )}
              </>
            )}
          </DecisionPanel>
        }
      />

      <ConfirmAction
        open={confirmApprove}
        title="Approve KYC?"
        message="Creates the customer record and a draft contract. Confirm only when identity and photos are acceptable."
        confirmLabel="Approve KYC"
        danger
        busy={mutation.isPending}
        onCancel={() => setConfirmApprove(false)}
        onConfirm={doApprove}
      />
    </>
  );
}

function DetailCard({ sub, photos }: { sub: KycSubmissionDetail; photos: string[] }) {
  const p = sub.payload;
  const coords =
    p.gps_lat !== null && p.gps_lat !== undefined && p.gps_lng !== null && p.gps_lng !== undefined
      ? `${p.gps_lat.toFixed(4)}, ${p.gps_lng.toFixed(4)}`
      : "Not captured";
  const accuracy =
    p.gps_accuracy_m !== null && p.gps_accuracy_m !== undefined
      ? ` · ±${Math.round(p.gps_accuracy_m)} m`
      : "";
  const nida = maskNida(p.national_id ?? sub.national_id);
  const photoSummary = [
    p.photo_customer_url ? "Customer ✓" : photos[0] ? `Customer (${photos[0]})` : null,
    p.photo_house_url ? "House ✓" : photos[1] ? `House (${photos[1]})` : null,
  ]
    .filter(Boolean)
    .join(" · ");

  const badge =
    sub.status === "pending" ? (
      <AppBadge tone="warning">
        <IconWarning size={12} /> Pending
      </AppBadge>
    ) : sub.status === "approved" ? (
      <AppBadge tone="success">
        <IconCheck size={12} /> Approved
      </AppBadge>
    ) : (
      <AppBadge tone="info">Correction requested</AppBadge>
    );

  return (
    <DetailPanel title={sub.customer_name ?? "—"} badge={badge} aria-label="Submission detail">
      <DetailRows
        rows={[
          { label: "NIDA", value: nida },
          { label: "Phone", value: p.phone ?? sub.phone ?? "—" },
          { label: "Ward", value: p.ward ?? sub.ward ?? "—" },
          { label: "Address", value: p.address ?? "—" },
          {
            label: "GPS",
            value: (
              <Stack direction="row" spacing={1} useFlexGap sx={{ justifyContent: { sm: "flex-end" }, flexWrap: "wrap" }}>
                <Typography variant="body2" component="span">
                  {coords}
                  {accuracy}
                </Typography>
                {geofenceBadge(p.geofence_state)}
              </Stack>
            ),
          },
          { label: "Photos", value: photoSummary || "—" },
        ]}
      />
      <PhotoGrid>
        <KycPhoto label="Customer photo" url={p.photo_customer_url} fallbackName={photos[0]} />
        <KycPhoto label="House photo" url={p.photo_house_url} fallbackName={photos[1]} />
      </PhotoGrid>
    </DetailPanel>
  );
}

function KycPhoto({
  label,
  url,
  fallbackName,
}: {
  label: string;
  url?: string | null;
  fallbackName?: string;
}) {
  const [failed, setFailed] = useState(false);
  if (url && !failed) {
    const href = fileUrl(url);
    return (
      <Box
        component="span"
        onErrorCapture={() => setFailed(true)}
        sx={{ display: "contents" }}
      >
        <PhotoThumb label={label} href={href} />
      </Box>
    );
  }
  return (
    <PhotoThumb
      label={label}
      failedFallback={
        <Stack spacing={0.5} sx={{ alignItems: "center" }}>
          <IconCamera size={16} />
          <span>{fallbackName ?? label}</span>
        </Stack>
      }
    />
  );
}
